import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, ListChecks } from "lucide-react";
import { useAuth } from "@/lib/auth";

const REDIRECT_SECONDS = 5;

export default function Welcome() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Captured once: only reachable via the signup-complete redirect.
  const [allowed] = useState(() => !!location.state?.fromSignup);
  const [seconds, setSeconds] = useState(REDIRECT_SECONDS);

  const fired = useRef(false);

  useEffect(() => {
    if (!allowed || fired.current) return;
    fired.current = true;
    sessionStorage.removeItem("calibrate_signup_welcome");
    window.history.replaceState({}, "");
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "signup_complete", page_path: "/welcome" });
  }, [allowed]);

  useEffect(() => {
    if (!allowed) return;
    if (seconds <= 0) { navigate("/dashboard", { replace: true, state: { showGuide: true } }); return; }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [allowed, seconds, navigate]);

  if (!allowed) return <Navigate to="/dashboard" replace />;

  const firstName = user?.name?.split(" ")[0] || "there";

  return (
    <div className="max-w-xl mx-auto px-6 py-20 sm:py-28" data-testid="welcome-page">
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] mb-6">[ACCOUNT CREATED]</div>
      <CheckCircle2 size={56} className="text-[#FF4F00] mb-6" data-testid="welcome-check-icon" />
      <h1 className="font-display font-bold text-4xl sm:text-5xl mb-4" data-testid="welcome-heading">
        Welcome, {firstName}.
      </h1>
      <p className="text-[#666] text-sm sm:text-base mb-10" data-testid="welcome-message">
        Your account is ready. We're taking you to your dashboard where you can create your first employee profile and start calibrating.
      </p>
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="button"
          onClick={() => navigate("/dashboard", { replace: true, state: { showGuide: true } })}
          className="inline-flex items-center justify-center gap-2 bg-[#FF4F00] text-white px-6 py-3 font-mono-cal text-xs uppercase tracking-[0.15em] hover:bg-[#111] transition-colors"
          data-testid="welcome-guide-btn"
        >
          <ListChecks size={14} /> See your 6-step guide
        </button>
        <button
          type="button"
          onClick={() => navigate("/dashboard", { replace: true })}
          className="inline-flex items-center justify-center gap-2 border border-[#111] text-[#111] px-6 py-3 font-mono-cal text-xs uppercase tracking-[0.15em] hover:bg-[#111] hover:text-white transition-colors"
          data-testid="welcome-continue-btn"
        >
          Go to dashboard now <ArrowRight size={14} />
        </button>
        <span className="font-mono-cal text-xs text-[#666]" data-testid="welcome-countdown">
          Redirecting in {seconds}s…
        </span>
      </div>
    </div>
  );
}
